/**
 * Complaints Management Page
 * Manage and review all user complaints across the platform
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    Chip,
    Link,
    SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination, CopyableText } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox } from '../../components/ui';
import { ComplaintService } from '../../services/complaint/ComplaintService';
import { formatDateTime } from '../../utils/dateUtils';
import type {
    Complaint,
    ComplaintStatus,
    ComplaintCategory,
    PaginatedComplaints,
} from '../../types/complaint';

type StatusFilter = ComplaintStatus | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Under Review' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'rejected', label: 'Rejected' },
];

const statusLabels: Record<ComplaintStatus, string> = {
    pending: 'Under Review',
    resolved: 'Resolved',
    rejected: 'Rejected',
};

const categoryLabels: Record<ComplaintCategory, string> = {
    harassment: 'Harassment',
    inappropriate_content: 'Inappropriate Content',
    spam: 'Spam',
    fraud: 'Fraud',
    other: 'Other',
};

const getStatusColor = (status: ComplaintStatus, theme: any): string => {
    switch (status) {
        case 'pending':
            return theme.palette.warning.main;
        case 'resolved':
            return theme.palette.success.main;
        case 'rejected':
            return theme.palette.error.main;
        default:
            return theme.palette.text.secondary;
    }
};

const getCategoryColor = (category: ComplaintCategory, theme: any): string => {
    switch (category) {
        case 'harassment':
            return theme.palette.error.main;
        case 'inappropriate_content':
            return theme.palette.error.light;
        case 'spam':
            return theme.palette.warning.main;
        case 'fraud':
            return theme.palette.error.dark;
        case 'other':
            return theme.palette.grey[500];
        default:
            return theme.palette.grey[600];
    }
};

const SEARCH_DEBOUNCE_MS = 400;

const ComplaintsManagementPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [complaints, setComplaints] = useState<Complaint[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<Set<ComplaintStatus>>(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalResults, setTotalResults] = useState(0);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchValue]);

    const fetchComplaints = useCallback(async () => {
        try {
            setIsLoading(true);
            const result: PaginatedComplaints = await ComplaintService.getComplaints(
                page,
                pageSize,
                {
                    status: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : 'all',
                    search: debouncedSearchValue || undefined,
                }
            );
            setComplaints(result.data);
            setTotalResults(result.total);
        } catch (error) {
            console.error('Failed to fetch complaints:', error);
            setComplaints([]);
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, selectedStatuses, debouncedSearchValue]);

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        setPage(1);
    };

    const handleStatusToggle = (status: ComplaintStatus) => {
        const newSelected = new Set(selectedStatuses);
        if (newSelected.has(status)) {
            newSelected.delete(status);
        } else {
            newSelected.add(status);
        }
        setSelectedStatuses(newSelected);
        setPage(1);
    };

    const handleViewDetails = (complaint: Complaint) => {
        navigate(`/complaints/${complaint.id}`);
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    return (
        <MainLayout>
            {/* Page Header */}
            <Box sx={{ marginBottom: theme.spacing(3) }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        marginBottom: theme.spacing(1),
                    }}
                >
                    Complaints Management
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: theme.palette.text.secondary }}
                >
                    Review and resolve user complaints about content and behavior.
                </Typography>
            </Box>

            {/* Search and Filters */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.spacing(3),
                    flexWrap: 'wrap',
                    gap: theme.spacing(2),
                }}
            >
                {/* Search Box */}
                <SearchBox
                    placeholder="Search by Mission Code, Title or Call Sign..."
                    value={searchValue}
                    onChange={handleSearch}
                    containerSx={{ flex: 1, minWidth: 300 }}
                />

                {/* Status Filters (Multi-select with Chips) */}
                <Box sx={{ width: '100%', marginBottom: theme.spacing(1) }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(1.5),
                            color: theme.palette.text.primary,
                            fontSize: '0.875rem',
                        }}
                    >
                        Filter by Status:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                        {statusFilters.filter(f => f.value !== 'all').map((option) => {
                            const isSelected = selectedStatuses.has(option.value as ComplaintStatus);
                            return (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    onClick={() => handleStatusToggle(option.value as ComplaintStatus)}
                                    onDelete={isSelected ? (e) => {
                                        e.stopPropagation();
                                        handleStatusToggle(option.value as ComplaintStatus);
                                    } : undefined}
                                    clickable
                                    sx={{
                                        backgroundColor: isSelected
                                            ? theme.palette.primary.main
                                            : theme.palette.background.paper,
                                        color: isSelected
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                        fontWeight: 500,
                                        borderRadius: theme.shapes.borderRadius.lg,
                                        border: `1px solid ${isSelected
                                            ? theme.palette.primary.main
                                            : (theme as any).customColors.tableBorder
                                            }`,
                                        '&:hover': {
                                            backgroundColor: isSelected
                                                ? theme.palette.primary.dark
                                                : (theme as any).customColors.tableRowHover,
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>

            </Box>

            {/* Complaints Table */}
            <Card>
                <CardContent noPadding>
                    {isLoading ? (
                        <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
                            <LoadingSpinner />
                        </Box>
                    ) : complaints.length === 0 ? (
                        <Box sx={{ padding: theme.spacing(4) }}>
                            <EmptyState
                                title="No Complaints Found"
                                message="There are no complaints matching your criteria."
                            />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table sx={{ minWidth: 1200 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell>Mission Code</TableHeaderCell>
                                            <TableHeaderCell>Mission Title</TableHeaderCell>
                                            <TableHeaderCell>Category</TableHeaderCell>
                                            <TableHeaderCell>Status</TableHeaderCell>
                                            <TableHeaderCell>Reporter</TableHeaderCell>
                                            <TableHeaderCell>Reported User</TableHeaderCell>
                                            <TableHeaderCell>Created At</TableHeaderCell>
                                            <TableHeaderCell>Action</TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {complaints.map((complaint) => (
                                            <TableRow key={complaint.id}>
                                                <TableCell>
                                                    <CopyableText
                                                        text={complaint.missionCode}
                                                        onTextClick={() => navigate(`/missions/${complaint.missionId}`)}
                                                        sx={{ color: theme.palette.primary.main, fontWeight: 400 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                        {complaint.missionTitle}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-block',
                                                            padding: theme.spacing(0.5, 1.5),
                                                            borderRadius: theme.shapes.borderRadius.lg,
                                                            backgroundColor: getCategoryColor(complaint.category, theme),
                                                            color: theme.palette.common.white,
                                                            fontWeight: 500,
                                                            fontSize: theme.typography.caption.fontSize,
                                                        }}
                                                    >
                                                        {categoryLabels[complaint.category] || complaint.category}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-block',
                                                            padding: theme.spacing(0.5, 1.5),
                                                            borderRadius: theme.shapes.borderRadius.lg,
                                                            backgroundColor: getStatusColor(complaint.status, theme),
                                                            color: theme.palette.common.white,
                                                            fontWeight: 500,
                                                            fontSize: theme.typography.caption.fontSize,
                                                        }}
                                                    >
                                                        {statusLabels[complaint.status]}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                        {complaint.reporterName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                        {complaint.reporterEmail}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                        {complaint.reportedUserName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                        {complaint.reportedUserEmail}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                    {formatDateTime(complaint.createdAt)}
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        onClick={() => handleViewDetails(complaint)}
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            cursor: 'pointer',
                                                            textDecoration: 'none',
                                                            fontSize: '0.875rem',
                                                            fontWeight: 400,
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        View
                                                    </Link>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <TablePagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalResults}
                                onPageChange={setPage}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </MainLayout>
    );
};

export default ComplaintsManagementPage;
